import {PropsWithChildren, useEffect, useMemo, useState} from "react";
import {useDocument} from "react-firebase-hooks/firestore";
import {collection, doc, onSnapshot, query, Timestamp, where} from "firebase/firestore";
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {selectCalendarFirstDate, selectCalendarVisibleDays, selectDepartment} from "../features/AppStateSlice";
import {setWarehouseLocks} from "../features/AppDataSlice";

import {addCalendarDays, WarehouseDayLock, WarehouseJobState, WarehouseLocksState,
    warehouseDayKey} from "../app/warehouse/warehouseLocks";
import {dateToString} from "../app/tools";

const emptyJob: WarehouseJobState = {
    enabled: false, initialized: false, startDay: "", lastCompletedRunDay: "",
    completedAt: 0, status: "", error: "",
};

export const FirebaseWarehouseLocksLoader = (props: PropsWithChildren<{}>) => {
    const dispatch = useAppDispatch();
    const calendarFirstDate = useAppSelector(selectCalendarFirstDate);
    const calendarDays = useAppSelector(selectCalendarVisibleDays);
    const department = useAppSelector(selectDepartment);
    const firstDay = warehouseDayKey(dateToString(new Date(calendarFirstDate)));
    const lastDay = addCalendarDays(firstDay, calendarDays + 1);
    const scope = `${department}:${firstDay}:${lastDay}`;
    const jobRef = useMemo(() => doc(appFirestore, "systemJobs/warehouseAutoLock"), []);
    const [jobDocument, jobLoading, jobError] = useDocument(jobRef, {
        snapshotListenOptions: {includeMetadataChanges: true},
    });
    const job = useMemo<WarehouseJobState>(() => {
        if (!jobDocument?.exists()) return emptyJob;
        const completedAt = jobDocument.get("completedAt");
        const leaseUntil = jobDocument.get("leaseUntil");
        return {
            enabled: jobDocument.get("enabled") === true,
            initialized: jobDocument.get("initialized") === true,
            startDay: String(jobDocument.get("startDay") || ""),
            lastCompletedRunDay: String(jobDocument.get("lastCompletedRunDay") || ""),
            completedAt: completedAt instanceof Timestamp ? completedAt.toMillis() : 0,
            leaseUntil: leaseUntil instanceof Timestamp ? leaseUntil.toMillis() : 0,
            status: String(jobDocument.get("status") || ""), error: String(jobDocument.get("error") || ""),
        };
    }, [jobDocument]);
    const [source, setSource] = useState<WarehouseLocksState>({data: [], loading: true});
    const [now, setNow] = useState(Date.now);
    const [jobTimedOut, setJobTimedOut] = useState(false);

    useEffect(() => {
        if (jobDocument && !jobDocument.metadata.fromCache) {
            setJobTimedOut(false);
            return;
        }
        const timer = setTimeout(() => setJobTimedOut(true), 10000);
        return () => clearTimeout(timer);
    }, [jobDocument]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const tick = () => {
            setNow(Date.now());
            timer = setTimeout(tick, 60000 - Date.now() % 60000);
        };
        tick();
        const resume = () => setNow(Date.now());
        window.addEventListener("focus", resume);
        document.addEventListener("visibilitychange", resume);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("focus", resume);
            document.removeEventListener("visibilitychange", resume);
        };
    }, []);

    useEffect(() => {
        let active = true;
        let confirmed = false;
        const base = {scope, department, firstDay, lastDay};
        setSource(previous => previous.scope === scope ? previous : {...base, data: [], loading: true});
        if (!["montage", "catering", "store"].includes(department)) {
            setSource({...base, data: [], loading: false, confirmedCycle: job.completedAt});
            return;
        }
        // Reattach after job completion; only a new server snapshot crosses the barrier.
        const locksQuery = query(collection(appFirestore, "warehouseLocks"),
            where("date", ">=", new Date(`${addCalendarDays(firstDay, -1)}T00:00:00Z`)),
            where("date", "<", new Date(`${lastDay}T00:00:00Z`)));
        const timeout = setTimeout(() => {
            if (active && !confirmed) setSource({...base, data: [], loading: false,
                error: "Няма потвърдена връзка за заключванията. Проверете интернет връзката."});
        }, 10000);
        const unsubscribe = onSnapshot(locksQuery, {includeMetadataChanges: true}, snapshot => {
            if (!active) return;
            if (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites) {
                if (confirmed) setSource({...base, data: [], loading: false,
                    error: "Заключванията не са потвърдени от сървъра. Проверете връзката."});
                return;
            }
            confirmed = true;
            clearTimeout(timeout);
            const records: Record<string, WarehouseDayLock> = {};
            snapshot.docs.forEach(value => {
                const until = value.get(`temporaryUnlocks.${department}.until`);
                records[value.id] = {locked: value.get(department) === true,
                    unlockUntil: until instanceof Timestamp ? until.toMillis() : 0};
            });
            setSource({...base, records, data: Object.keys(records).filter(id => records[id].locked),
                loading: false, confirmedCycle: job.completedAt});
        }, error => {
            if (active) setSource({...base, data: [], loading: false,
                error: `Неуспешно зареждане на заключванията: ${error.message}`});
        });
        return () => { active = false; clearTimeout(timeout); unsubscribe(); };
    }, [scope, department, firstDay, lastDay, job.completedAt]);

    useEffect(() => {
        const stale = source.scope !== scope;
        const serverJob = !!jobDocument && !jobDocument.metadata.fromCache;
        dispatch(setWarehouseLocks({...source, scope, department, firstDay, lastDay, job, now,
            loading: !jobError && !jobTimedOut && !source.error && (jobLoading || !serverJob || stale || source.loading),
            error: jobError || jobTimedOut ? "Неуспешно зареждане на автоматичното заключване. Проверете връзката." : source.error,
        }));
    }, [dispatch, source, scope, department, firstDay, lastDay, job, now, jobLoading, jobError, jobDocument, jobTimedOut]);
    return <>{props.children}</>;
};
